import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, BookOpen, PlayCircle, FileText, Globe2, Mail, User, Rocket, ExternalLink } from "lucide-react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { AspectRatio } from "../components/ui/aspect-ratio";
import { apiClient, API } from "../lib/api";
import { installScormApi } from "../lib/scormApi";
import { useAuth } from "../auth/AuthContext";
import CourseReviews from "./CourseReviews";
import CourseQuiz from "./CourseQuiz";

const REQUEST_ACCESS_EMAIL = "dpigovernanceucl@gmail.com";

function buildMailto(caseTitle) {
  const subject = encodeURIComponent(`Case study access request: ${caseTitle}`);
  const body = encodeURIComponent(
    `Hello DPI Applied Learning team,\n\nI would like to request access to the case study \"${caseTitle}\" for teaching / learning purposes.\n\nA few details about me:\n- Name:\n- Organisation:\n- Role:\n- Intended use:\n\nThank you,\n`
  );
  return `mailto:${REQUEST_ACCESS_EMAIL}?subject=${subject}&body=${body}`;
}

function RequestAccessMailto({ caseId, caseTitle }) {
  const onClick = () => {
    // Best-effort telemetry; still proceeds to mailto.
    apiClient.post("/case-access-request", { case_id: caseId }).catch(() => {});
  };
  return (
    <Button asChild size="sm" className="bg-[#361a54] text-white hover:bg-[#2a1443]" data-testid="request-case-access-btn">
      <a href={buildMailto(caseTitle)} onClick={onClick}>
        <Mail className="mr-1.5 h-3.5 w-3.5" />
        Request case access
      </a>
    </Button>
  );
}

export function LandingPage() {
  return (
    <div data-testid="landing-page" className="flex flex-1 flex-col gap-8 md:gap-10">
      <section className="hero-section">
        <div className="hero-inner flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
          <div className="max-w-xl space-y-4">
            <h1 className="text-balance text-3xl font-semibold tracking-tight text-white md:text-4xl lg:text-[2.75rem]">
              Learning to govern Digital Public Infrastructure, together.
            </h1>
          </div>
          <div className="mt-6 flex w-full max-w-sm flex-col gap-3 md:mt-0">
            <Link to="/learn" data-testid="hero-course-card" className="glass-panel relative flex flex-col px-4 py-4 transition-transform duration-150 ease-out hover:-translate-y-0.5">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-100/80">Course</p>
              <p className="mt-1 min-h-[2.5rem] text-sm font-semibold text-white">DPI Governance applied learning</p>
              <p className="mt-1 min-h-[3.25rem] text-xs leading-relaxed text-slate-100/90">A self-paced online course introducing DPI governance concepts, risks and practical tools.</p>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-100/90">
                <span>3–4 hours · Online</span>
                <span className="inline-flex items-center gap-1 text-xs font-medium">Learn more<ArrowRight className="h-3.5 w-3.5" /></span>
              </div>
            </Link>
            <Link to="/case-studies" data-testid="hero-casestudies-card" className="glass-panel relative flex flex-col px-4 py-4 transition-transform duration-150 ease-out hover:-translate-y-0.5">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-100/80">Case study library</p>
              <p className="mt-1 min-h-[2.5rem] text-sm font-semibold text-white">DPI and digital transformation in practice</p>
              <p className="mt-1 min-h-[3.25rem] text-xs leading-relaxed text-slate-100/90">Short video cases and written materials from UCL IIPP and partners on DPI and digital transformation.</p>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-100/90">
                <span>Multiple themes · Global coverage</span>
                <span className="inline-flex items-center gap-1 text-xs font-medium">Explore cases<ArrowRight className="h-3.5 w-3.5" /></span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <section className="space-y-3 md:space-y-4">
        <div className="space-y-1">
          <h2 className="text-base font-semibold tracking-tight text-[#361a54] md:text-lg">Additional resources</h2>
          <p className="max-w-prose text-sm text-muted-foreground">
            Separate but related efforts that complement the DPI Governance Applied Learning site.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-3 md:gap-6">
          <Card className="card-elevated flex flex-col border border-[#d4c5e6] !bg-[#efe6f8]" data-testid="resource-card-workshops">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base font-semibold text-[#361a54]">
                <BookOpen className="h-4 w-4 text-[#1a8bb3]" />
                In person workshops
              </CardTitle>
              <CardDescription className="text-sm leading-relaxed">
                We conduct in-person programmes with public servants and partners. Reach out to discuss what would be most useful for your team.
              </CardDescription>
            </CardHeader>
            <CardContent className="mt-auto flex items-center justify-between gap-3 border-t border-[#d4c5e6] pt-3 text-xs text-muted-foreground">
              <p>Contact us for further details and availability.</p>
              <Button asChild size="sm" className="bg-[#361a54] text-white hover:bg-[#2a1443]">
                <a href="mailto:dpigovernanceucl@gmail.com?subject=In%20person%20workshops%20enquiry">Email us<ArrowRight className="ml-1.5 h-3.5 w-3.5" /></a>
              </Button>
            </CardContent>
          </Card>

          <Card className="card-elevated flex flex-col" data-testid="resource-card-dpi-map">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base font-semibold text-[#361a54]">
                <PlayCircle className="h-4 w-4 text-[#1a8bb3]" />
                DPI Map
              </CardTitle>
              <CardDescription className="text-sm leading-relaxed">
                Explore how countries are building DPI functions such as identity, payments and data exchange through the DPI
                Map project. Developed as part of UCL IIPP Digital team.
              </CardDescription>
            </CardHeader>
            <CardContent className="mt-auto flex items-center justify-between gap-3 border-t border-[#f5f0fa] pt-3 text-xs text-muted-foreground">
              <div />
              <Button asChild size="sm" variant="outline" className="border-[#d4c5e6] bg-[#f5f0fa] text-[#361a54] hover:bg-[#e8e0f0]">
                <a href="https://dpimap.org" target="_blank" rel="noreferrer">Visit DPI Map<ArrowRight className="ml-1.5 h-3.5 w-3.5" /></a>
              </Button>
            </CardContent>
          </Card>

          <Card className="card-elevated flex flex-col" data-testid="resource-card-tpsda">
            <CardHeader className="pb-3">
              <CardTitle className="flex flex-col gap-0.5 text-base font-semibold text-[#361a54]">
                <span className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-[#1a8bb3]" />
                  TPSDA
                </span>
                <span className="text-xs font-medium normal-case text-[#4a2d73]">Teaching Public Service in the Digital Age</span>
              </CardTitle>
              <CardDescription className="text-sm leading-relaxed">
                Interested in the digital skills governments need today? Do check out TPSDA's open access material.
              </CardDescription>
            </CardHeader>
            <CardContent className="mt-auto flex items-center justify-between gap-3 border-t border-[#f5f0fa] pt-3 text-xs text-muted-foreground">
              <p>Explore open-access learning material for public servants.</p>
              <Button asChild size="sm" variant="outline" className="border-[#d4c5e6] bg-[#f5f0fa] text-[#361a54] hover:bg-[#e8e0f0]">
                <a href="https://www.teachingpublicservice.digital/digital-government-academic-leadership-program" target="_blank" rel="noreferrer">Visit TPSDA<ArrowRight className="ml-1.5 h-3.5 w-3.5" /></a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}

export function CoursePage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ workshop_text: "" });
  const [myProgress, setMyProgress] = useState(null);

  useEffect(() => {
    apiClient.get("/courses").then((r) => {
      setCourses(r.data);
      setLoading(false);
    });
    apiClient.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  // Load this learner's progress for the current course (for Resume / status)
  useEffect(() => {
    if (!user || !courseId) {
      setMyProgress(null);
      return;
    }
    apiClient.get("/me/progress").then((r) => {
      const p = (r.data || []).find((x) => x.course_id === courseId);
      setMyProgress(p || null);
    }).catch(() => setMyProgress(null));
  }, [user, courseId]);

  // Track course view when courseId in URL
  useEffect(() => {
    if (courseId) {
      apiClient.post("/events", { type: "course_view", entity_id: courseId }).catch(() => {});
    }
  }, [courseId]);

  if (loading) {
    return <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>;
  }

  if (!courseId) {
    return (
      <div data-testid="course-index-page" className="flex flex-1 flex-col gap-8 md:gap-10">
        <section className="space-y-4">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#4a2d73]">Learn DPI now</p>
          <h1 className="text-balance text-2xl font-semibold tracking-tight text-[#361a54] md:text-3xl">DPI Governance e-learning courses</h1>
          <p className="max-w-prose text-sm text-muted-foreground md:text-base">
            Select a course card to open its dedicated page with learning objectives, who it is for and how to access the content.
          </p>
          <CcLicense variant="notice" />

          <div className="mt-3 grid gap-3">
            {courses.map((course) => {
              const isMain = !!course.articulate_url || !!course.content_entry_path;
              return (
                <Card key={course.course_id} data-testid={`course-card-${course.course_id}`} className="card-elevated border border-[#e8e0f0] bg-white/90 hover:bg-[#f5f0fa]/70">
                  <Link to={`/learn/${course.course_id}`} className="flex h-full w-full flex-col items-stretch text-left">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base font-semibold text-[#361a54]">{course.title}</CardTitle>
                      <CardDescription className="mt-1 text-sm text-muted-foreground">{course.short_description}</CardDescription>
                    </CardHeader>
                    <CardContent className="mt-auto flex items-center justify-between gap-2 pb-3 text-xs text-slate-700">
                      <span>{course.duration}</span>
                      <span className={isMain ? "rounded-full bg-[#361a54] px-2.5 py-0.5 text-xs font-semibold text-white" : "text-xs font-medium text-[#361a54]"}>
                        {course.status}
                      </span>
                    </CardContent>
                  </Link>
                </Card>
              );
            })}
          </div>
        </section>

        <section className="space-y-3 md:space-y-4" data-testid="workshop-section">
          <div className="space-y-1">
            <h2 className="text-base font-semibold tracking-tight text-[#361a54] md:text-lg">In person training / workshop</h2>
          </div>
          <Card className="card-elevated border border-[#d4c5e6] !bg-[#efe6f8]">
            <CardContent className="flex flex-col gap-4 py-5 sm:flex-row sm:items-center sm:justify-between">
              <p className="max-w-prose whitespace-pre-line text-sm leading-relaxed text-slate-700 md:text-base" data-testid="workshop-text">
                {settings.workshop_text || "We run in-person training and workshops tailored for your team. Contact us to discuss dates and content."}
              </p>
              <Button asChild size="sm" className="shrink-0 bg-[#361a54] text-white hover:bg-[#2a1443]" data-testid="workshop-email-btn">
                <a href="mailto:dpigovernanceucl@gmail.com?subject=In%20person%20training%20%2F%20workshop%20enquiry">
                  <Mail className="mr-1.5 h-3.5 w-3.5" />
                  Email us<ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                </a>
              </Button>
            </CardContent>
          </Card>
        </section>
      </div>
    );
  }

  const selected = courses.find((c) => c.course_id === courseId) || courses[0];
  if (!selected) {
    return <p className="py-10 text-center text-sm text-muted-foreground">Course not found.</p>;
  }
  const hasContent = !!selected.content_entry_path;
  const hasUrl = !!selected.articulate_url;
  const progressStatus = myProgress?.status;
  const isInProgress = progressStatus === "in_progress";
  const isCompleted = progressStatus === "completed";

  const startCourse = async () => {
    apiClient.post("/events", { type: "course_start", entity_id: selected.course_id }).catch(() => {});
    // Login is required only to play the course / open Articulate, not to view details.
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(`/learn/${selected.course_id}`)}`);
      return;
    }
    if (!isCompleted) {
      try {
        await apiClient.put(`/me/progress/${selected.course_id}`, { status: "in_progress" });
      } catch {}
    }
    if (hasContent) {
      navigate(`/learn/${selected.course_id}/play`);
    } else if (hasUrl) {
      window.open(selected.articulate_url, "_blank", "noopener,noreferrer");
    }
  };

  const primaryLabel = !user
    ? "Sign in to start"
    : isCompleted
    ? "Review course"
    : isInProgress
    ? "Resume course"
    : "Start course";

  return (
    <div data-testid="course-detail-page" className="flex flex-1 flex-col gap-8 md:gap-10">
      <section className="space-y-4 md:space-y-5">
        <div className="flex items-center gap-3">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#4a2d73]">Course</p>
          {user && progressStatus && (
            <span
              data-testid="course-status-pill"
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                isCompleted
                  ? "border border-emerald-200 bg-emerald-50 text-emerald-800"
                  : isInProgress
                  ? "border border-[#d4c5e6] bg-[#efe6f8] text-[#361a54]"
                  : "border border-[#e8e0f0] bg-[#f5f0fa] text-[#4a2d73]"
              }`}
            >
              {isCompleted ? "Completed" : isInProgress ? "In progress" : "Enrolled"}
            </span>
          )}
        </div>
        <h1 className="text-balance text-2xl font-semibold tracking-tight text-[#361a54] md:text-3xl">{selected.title}</h1>

        <div className="flex flex-wrap items-center gap-3 pt-1">
          {(hasContent || hasUrl) ? (
            <>
              <Button onClick={startCourse} className="bg-[#361a54] text-white hover:bg-[#2a1443] text-sm md:text-base px-4 py-2" size="sm" data-testid="start-course-btn">
                {primaryLabel}<ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
              <p className="text-sm text-muted-foreground">
                {hasContent ? "Opens the course in a new window." : "Opens in a new tab on Articulate 360."}
              </p>
            </>
          ) : (
            <>
              <Button disabled size="sm" data-testid="start-course-btn-disabled" className="cursor-not-allowed bg-[#c4b5d9] text-[#5b3d85] hover:bg-[#c4b5d9]">
                Start course – coming soon
              </Button>
              <p className="text-sm text-muted-foreground">
                This e-learning course is currently in development. The outline below is indicative and may change.
              </p>
            </>
          )}
        </div>
      </section>

      <section data-testid="course-main-layout" className="grid gap-6 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] md:items-start">
        <div className="space-y-4 md:space-y-5">
          {(() => {
            const visibility = selected.section_visibility || {};
            const labels = selected.section_labels || {};
            const T = (k, def) => labels[k] || def;
            const customSections = selected.custom_sections || [];
            const customById = {};
            customSections.forEach((s) => { customById[s.key] = s; });
            let order = (selected.section_order && selected.section_order.length)
              ? [...selected.section_order]
              : ["key_highlights", "learning_objectives", "instructor", "who_its_for", "structure", "quiz", "reviews"];
            // Ensure any custom sections not already in the saved order still render.
            customSections.forEach((s) => { if (!order.includes(s.key)) order.push(s.key); });
            const isVisible = (k) => visibility[k] !== false;
            // Multi-instructor list: prefer new `instructors` array; fall back to legacy single fields.
            const legacyInstructor = (selected.instructor_name || selected.instructor_bio || selected.instructor_photo_url)
              ? [{ name: selected.instructor_name || "", bio: selected.instructor_bio || "", photo_url: selected.instructor_photo_url || "" }]
              : [];
            const instructors = (selected.instructors && selected.instructors.length > 0) ? selected.instructors : legacyInstructor;
            const hasInstructor = instructors.length > 0;
            const quizEnabled = !!(selected.quiz && selected.quiz.enabled && (selected.quiz.questions || []).length > 0);
            const renderers = {
              key_highlights: () => selected.key_highlights?.length > 0 && (
                <Card key="kh" className="card-elevated" data-testid="card-key-highlights">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold text-[#361a54]">{T("key_highlights", "Key Highlights")}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm leading-relaxed text-slate-700">
                    <ul className="list-disc space-y-1 pl-4">
                      {selected.key_highlights.map((h, i) => (<li key={i}>{h}</li>))}
                    </ul>
                  </CardContent>
                </Card>
              ),
              learning_objectives: () => selected.learning_objectives?.length > 0 && (
                <Card key="lo" className="card-elevated" data-testid="card-what-you-will-learn">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold text-[#361a54]">{T("learning_objectives", "What you will learn")}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm leading-relaxed text-slate-700">
                    <ul className="list-disc space-y-1 pl-4">
                      {selected.learning_objectives.map((item, i) => (<li key={i}>{item}</li>))}
                    </ul>
                  </CardContent>
                </Card>
              ),
              instructor: () => hasInstructor && (
                <Card key="in" className="card-elevated" data-testid="card-instructor">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base font-semibold text-[#361a54]">
                      <User className="h-4 w-4 text-[#1a8bb3]" />{T("instructor", instructors.length > 1 ? "Course instructors" : "Course instructor")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 text-sm leading-relaxed text-slate-700">
                    {instructors.map((ins, idx) => (
                      <div key={idx} className={`flex flex-col gap-3 sm:flex-row sm:items-start ${idx > 0 ? "border-t border-[#e8e0f0] pt-4" : ""}`} data-testid={`instructor-${idx}`}>
                        {ins.photo_url ? (
                          <img
                            src={ins.photo_url.startsWith("http") ? ins.photo_url : `${API.replace(/\/api$/, "")}${ins.photo_url}`}
                            alt={ins.name || "Course instructor"}
                            className="h-24 w-24 shrink-0 rounded-full border border-[#e8e0f0] object-cover"
                            data-testid={`instructor-photo-${idx}`}
                          />
                        ) : (
                          <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full border border-[#e8e0f0] bg-[#f5f0fa] text-[#4a2d73]">
                            <User className="h-8 w-8" />
                          </div>
                        )}
                        <div className="space-y-1">
                          {ins.name && (
                            <p className="text-sm font-semibold text-[#361a54]" data-testid={`instructor-name-${idx}`}>{ins.name}</p>
                          )}
                          {ins.bio && (
                            <p className="whitespace-pre-line text-sm text-slate-700" data-testid={`instructor-bio-${idx}`}>{ins.bio}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ),
              who_its_for: () => selected.who_its_for?.length > 0 && (
                <Card key="wf" className="card-elevated" data-testid="card-who-this-is-for">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold text-[#361a54]">{T("who_its_for", "Who this is for")}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm leading-relaxed text-slate-700">
                    <ul className="list-disc space-y-1 pl-4">
                      {selected.who_its_for.map((item, i) => (<li key={i}>{item}</li>))}
                    </ul>
                  </CardContent>
                </Card>
              ),
              structure: () => selected.structure?.length > 0 && (
                <Card key="st" className="card-elevated" data-testid="card-course-structure">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold text-[#361a54]">{T("structure", "Course structure")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Accordion type="single" collapsible className="w-full">
                      {selected.structure.map((module, index) => (
                        <AccordionItem key={index} value={`module-${index + 1}`}>
                          <AccordionTrigger className="text-sm font-medium text-[#361a54]">{module.title}</AccordionTrigger>
                          <AccordionContent className="text-sm leading-relaxed text-slate-700">{module.description}</AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>
              ),
              quiz: () => quizEnabled && (
                <CourseQuiz key="qz" courseId={selected.course_id} passingScore={selected.quiz.passing_score || 70} onPassed={() => setMyProgress({ ...(myProgress || {}), course_id: selected.course_id, status: "completed" })} />
              ),
              reviews: () => <CourseReviews key="rv" courseId={selected.course_id} />,
            };
            return order.filter(isVisible).map((k) => {
              if (renderers[k]) return renderers[k]();
              const cs = customById[k];
              if (cs && (cs.items || []).length > 0) {
                return (
                  <Card key={k} className="card-elevated" data-testid={`card-${k}`}>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base font-semibold text-[#361a54]">{cs.title || "Section"}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm leading-relaxed text-slate-700">
                      <ul className="list-disc space-y-1 pl-4">
                        {cs.items.map((it, i) => (<li key={i}>{it}</li>))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              }
              return null;
            });
          })()}
        </div>

        <aside className="space-y-4 md:sticky md:top-24">
          <Card className="card-elevated" data-testid="card-course-meta">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-[#361a54]">{(selected.section_labels || {}).about || "About this course"}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-slate-700">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#4a2d73]">Duration</p>
                <p className="mt-0.5 font-medium text-[#361a54]">{selected.duration || "—"}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#4a2d73]">Status</p>
                <p className="mt-0.5 font-medium text-[#361a54]">{selected.status}</p>
              </div>
              {selected.short_description && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#4a2d73]">Summary</p>
                  <p className="mt-0.5 text-sm leading-relaxed text-slate-700">{selected.short_description}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </aside>
      </section>
    </div>
  );
}

export function CoursePlayerPage() {
  const { courseId } = useParams();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [scormStatus, setScormStatus] = useState("");
  const [scormReady, setScormReady] = useState(false);
  const [resumed, setResumed] = useState(false);
  const [progressPct, setProgressPct] = useState(null);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const courseWindowRef = useRef(null);

  useEffect(() => {
    apiClient.get(`/courses/${courseId}`).then((r) => setCourse(r.data));
  }, [courseId]);

  useEffect(() => {
    if (user) {
      apiClient.put(`/me/progress/${courseId}`, { status: "in_progress" }).catch(() => {});
    }
  }, [user, courseId]);

  // Install SCORM 1.2 / 2004 API BEFORE the iframe mounts so the very first
  // synchronous GetValue('cmi.core.lesson_location') returns the resume slot.
  useEffect(() => {
    if (!user) return undefined;
    let uninstall = () => {};
    let cancelled = false;
    const handleState = (payload) => {
      const s = (payload?.lesson_status || "").toLowerCase();
      if (s) setScormStatus(s);
      if (payload?.resumed) setResumed(true);
      if (typeof payload?.progress_percent === "number") setProgressPct(payload.progress_percent);
      // Mark completed when SCORM reports completion OR when progress hits 100% (all modules visited)
      if (s === "completed" || s === "passed" || payload?.progress_percent === 100) setCompleted(true);
    };
    setScormReady(false);
    (async () => {
      const cleanup = await installScormApi({ apiBase: API, courseId, onStateChange: handleState });
      if (cancelled) { try { cleanup(); } catch {} return; }
      uninstall = cleanup;
      setScormReady(true);
    })();
    return () => { cancelled = true; try { uninstall(); } catch {} };
  }, [user, courseId]);

  // postMessage fallback for courses that don't speak SCORM but do dispatch completion events.
  useEffect(() => {
    if (!user) return undefined;
    let done = false;
    const markComplete = async () => {
      if (done) return;
      done = true;
      try {
        await apiClient.put(`/me/progress/${courseId}`, { status: "completed" });
        setCompleted(true);
      } catch {
        done = false;
      }
    };
    const isCompletionMessage = (data) => {
      if (!data) return false;
      if (typeof data === "string") {
        const s = data.toLowerCase();
        return s.includes("completed") || s.includes("lesson_status=completed") || s.includes("cmi.core.lesson_status=completed");
      }
      if (typeof data === "object") {
        const status = (data.status || data.completion_status || data.lessonStatus || data.lesson_status || "").toString().toLowerCase();
        if (status === "completed" || status === "passed") return true;
        if (data.type && ["complete", "completed", "course_complete", "course_completed"].includes(String(data.type).toLowerCase())) return true;
        if (data.cmi && (data.cmi.completion_status === "completed" || (data.cmi.core && data.cmi.core.lesson_status === "completed"))) return true;
        const verb = data?.verb?.id || data?.verb?.display?.en || data?.verb?.display?.["en-US"];
        if (typeof verb === "string" && /(completed|passed|finished)/i.test(verb)) return true;
      }
      return false;
    };
    const onMessage = (ev) => {
      if (isCompletionMessage(ev.data)) markComplete();
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [user, courseId]);

  if (!course) return <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>;

  if (!course.content_entry_path) {
    return (
      <div className="py-10 text-center">
        <p className="text-sm text-muted-foreground">No content uploaded yet for this course.</p>
        <Link to={`/learn/${courseId}`} className="mt-3 inline-block text-sm text-[#1a8bb3] hover:underline">Back to course</Link>
      </div>
    );
  }

  const src = `${API}/courses/${courseId}/content/${course.content_entry_path}`;

  const openCourseWindow = () => {
    if (courseWindowRef.current && !courseWindowRef.current.closed) {
      courseWindowRef.current.focus();
      setPopupBlocked(false);
      return;
    }
    const w = window.open(src, "dpi_course_window", "width=1280,height=860,resizable=yes,scrollbars=yes");
    if (!w) { setPopupBlocked(true); return; }
    courseWindowRef.current = w;
    setPopupBlocked(false);
  };

  return (
    <div data-testid="course-player" className="flex flex-1 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#4a2d73]">Now playing</p>
          <h1 className="text-base font-semibold text-[#361a54]">{course.title}</h1>
        </div>
        <div className="flex items-center gap-2">
          {resumed && !completed && (
            <span className="inline-flex items-center gap-1.5 rounded-md border border-[#d4c5e6] bg-[#f5f0fa] px-3 py-1 text-xs font-medium text-[#4a2d73]" data-testid="scorm-resume-pill">
              Resumed from last position
            </span>
          )}
          {typeof progressPct === "number" && !completed && (
            <span className="inline-flex items-center gap-1.5 rounded-md border border-[#d4c5e6] bg-white px-3 py-1 text-xs font-medium text-[#361a54]" data-testid="scorm-progress-pill">
              {progressPct}% complete
            </span>
          )}
          {scormStatus && !completed && !resumed && (
            <span className="inline-flex items-center gap-1.5 rounded-md border border-[#d4c5e6] bg-[#efe6f8] px-3 py-1 text-xs font-medium text-[#361a54]" data-testid="scorm-status-pill">
              SCORM: {scormStatus}
            </span>
          )}
          {completed && (
            <span className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-800" data-testid="course-completed-pill">
              Completion recorded
            </span>
          )}
          <Button onClick={openCourseWindow} size="sm" variant="outline" className="border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]" data-testid="open-course-window-btn">
            <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
            Open in new window
          </Button>
          <Button asChild size="sm" variant="outline" className="border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]" data-testid="back-to-course-btn">
            <Link to={`/learn/${courseId}`}>Back to course</Link>
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground" data-testid="course-completion-hint">
          Progress is recorded automatically and your last position is saved so you can resume where you left off.
        </p>
        <p className="text-xs text-muted-foreground" data-testid="popup-disclaimer">
          Course not displaying correctly? Use <span className="font-medium text-[#361a54]">Open in new window</span> (please allow pop-ups for this site).
        </p>
      </div>
      {popupBlocked && (
        <p className="text-xs font-medium text-red-700" data-testid="popup-blocked-msg">
          Your browser blocked the course window. Please allow pop-ups and click “Open in new window” again.
        </p>
      )}
      <div className="overflow-hidden rounded-xl border border-[#e8e0f0] bg-slate-900/95">
        {scormReady ? (
          <iframe
            src={src}
            title={course.title}
            className="h-[calc(100vh-220px)] min-h-[520px] w-full border-0"
            allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
            allowFullScreen
            data-testid="course-iframe"
          />
        ) : (
          <div className="flex h-[calc(100vh-220px)] min-h-[520px] w-full items-center justify-center text-sm text-slate-200" data-testid="course-iframe-loading">
            Loading your progress…
          </div>
        )}
      </div>
    </div>
  );
}

export function CaseStudiesPage() {
  const [cases, setCases] = useState([]);
  const [selectedTheme, setSelectedTheme] = useState("all");
  const [selectedCountry, setSelectedCountry] = useState("all");
  const [selectedAccess, setSelectedAccess] = useState("all");
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    apiClient.get("/case-studies").then((r) => {
      setCases(r.data);
      setSelectedId(r.data[0]?.case_id || null);
    });
  }, []);

  // Track when a case is opened (debounced via selectedId change)
  useEffect(() => {
    if (selectedId) {
      apiClient.post("/events", { type: "case_view", entity_id: selectedId }).catch(() => {});
    }
  }, [selectedId]);

  const themes = useMemo(() => ["all", ...Array.from(new Set(cases.map((c) => c.theme)))], [cases]);
  const countries = useMemo(() => ["all", ...Array.from(new Set(cases.map((c) => c.country)))], [cases]);

  const filtered = useMemo(() => cases.filter((c) => {
    const themeOk = selectedTheme === "all" || c.theme === selectedTheme;
    const countryOk = selectedCountry === "all" || c.country === selectedCountry;
    const accessOk =
      selectedAccess === "all" ||
      (selectedAccess === "open" && c.access_mode === "pdf") ||
      (selectedAccess === "request" && c.access_mode === "request");
    return themeOk && countryOk && accessOk;
  }), [cases, selectedTheme, selectedCountry, selectedAccess]);

  const selected = useMemo(() => filtered.find((c) => c.case_id === selectedId) || filtered[0] || null, [filtered, selectedId]);

  const trackPdfClick = (caseId) => {
    apiClient.post("/events", { type: "case_pdf_click", entity_id: caseId }).catch(() => {});
  };

  return (
    <div data-testid="case-studies-page" className="flex flex-1 flex-col gap-6 md:gap-8">
      <section className="space-y-3 md:space-y-4">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#4a2d73]">Case studies</p>
        <h1 className="text-balance text-2xl font-semibold tracking-tight text-[#361a54] md:text-3xl">
          Learn from DPI governance and digital transformation in practice.
        </h1>
        <p className="max-w-prose text-sm text-muted-foreground md:text-base">
          A curated set of short case studies from UCL IIPP and partners on how countries are designing, governing and using Digital Public Infrastructure.
        </p>
        <p className="max-w-prose text-sm text-amber-900">
          Case studies are for teaching and discussion purposes only and are not for distribution.
        </p>
      </section>

      <section className="grid gap-6 md:grid-cols-[minmax(0,1.25fr)_minmax(0,1.5fr)] md:items-start">
        <div className="space-y-3">
          <div className="flex flex-col gap-2 text-xs">
            <div className="flex flex-wrap gap-2">
              {themes.map((theme) => (
                <Button key={theme} type="button" variant={selectedTheme === theme ? "default" : "outline"} size="sm" data-testid={`filter-theme-${theme}`}
                  className={selectedTheme === theme ? "bg-[#361a54] text-white hover:bg-[#2a1443]" : "border-[#d4c5e6] bg-[#f5f0fa] text-[#361a54] hover:bg-[#e8e0f0]"}
                  onClick={() => setSelectedTheme(theme)}>
                  {theme === "all" ? "All themes" : theme}
                </Button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {countries.map((country) => (
                <Button key={country} type="button" variant={selectedCountry === country ? "default" : "outline"} size="sm" data-testid={`filter-country-${country}`}
                  className={selectedCountry === country ? "bg-[#361a54] text-white hover:bg-[#2a1443]" : "border-[#d4c5e6] bg-[#f5f0fa] text-[#361a54] hover:bg-[#e8e0f0]"}
                  onClick={() => setSelectedCountry(country)}>
                  {country === "all" ? "All countries" : country}
                </Button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { key: "all", label: "All access" },
                { key: "open", label: "Open access" },
                { key: "request", label: "On request" },
              ].map((opt) => (
                <Button
                  key={opt.key}
                  type="button"
                  variant={selectedAccess === opt.key ? "default" : "outline"}
                  size="sm"
                  data-testid={`filter-access-${opt.key}`}
                  className={selectedAccess === opt.key ? "bg-[#361a54] text-white hover:bg-[#2a1443]" : "border-[#d4c5e6] bg-[#f5f0fa] text-[#361a54] hover:bg-[#e8e0f0]"}
                  onClick={() => setSelectedAccess(opt.key)}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="mt-2 space-y-3">
            {filtered.map((item) => {
              const isActive = selected?.case_id === item.case_id;
              return (
                <Card key={item.case_id} data-testid={`case-item-${item.case_id}`} className={`case-list-item border border-[#e8e0f0]/70 bg-white/80 text-left ${isActive ? "case-list-item--active" : "hover:bg-[#f5f0fa]/70"}`} onClick={() => setSelectedId(item.case_id)}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="space-y-1">
                        <CardTitle className="text-sm font-semibold tracking-tight text-[#361a54]">{item.title}</CardTitle>
                        <CardDescription className="text-xs uppercase tracking-[0.18em] text-[#1a8bb3]">{item.organisation}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="flex items-end justify-between gap-3 pb-3">
                    <p className="line-clamp-2 text-sm text-slate-700">{item.summary}</p>
                    <div className="flex flex-col items-end gap-1 text-right text-xs text-[#4a2d73]">
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#f5f0fa] px-2 py-0.5"><Globe2 className="h-3.5 w-3.5" />{item.region}</span>
                      <span className="text-xs text-[#361a54]">{item.theme}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        <div className="space-y-4">
          {selected ? (
            <Card className="card-elevated overflow-hidden" data-testid="case-detail-card">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-base font-semibold text-[#361a54]">{selected.title}</CardTitle>
                    <CardDescription className="mt-1 text-sm text-[#4a2d73]">{selected.organisation}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <AspectRatio ratio={16 / 9} className="overflow-hidden rounded-xl bg-slate-900/95">
                  {selected.video_storage_path ? (
                    <video controls className="h-full w-full" src={`${API}/case-studies/${selected.case_id}/video`} data-testid="case-video">
                      Your browser does not support the video tag.
                    </video>
                  ) : selected.youtube_id ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${selected.youtube_id}`}
                      title={selected.title}
                      className="h-full w-full border-0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-slate-200">Video coming soon</div>
                  )}
                </AspectRatio>

                <div className="space-y-3 text-sm leading-relaxed text-slate-700">
                  {(selected.author_name || selected.author_affiliation || selected.author_photo_url) && (
                    <div className="flex flex-col gap-3 rounded-xl border border-[#e8e0f0] bg-[#f5f0fa]/40 p-3 sm:flex-row sm:items-start" data-testid="case-author">
                      {selected.author_photo_url ? (
                        <img
                          src={selected.author_photo_url.startsWith("http") ? selected.author_photo_url : `${API.replace(/\/api$/, "")}${selected.author_photo_url}`}
                          alt={selected.author_name || "Case author"}
                          className="h-16 w-16 shrink-0 rounded-full border border-[#e8e0f0] object-cover"
                          data-testid="case-author-photo"
                        />
                      ) : null}
                      <div className="space-y-0.5">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#4a2d73]">Author</p>
                        {selected.author_name && (
                          <p className="text-sm font-semibold text-[#361a54]" data-testid="case-author-name">{selected.author_name}</p>
                        )}
                        {selected.author_affiliation && (
                          <p className="text-xs text-slate-700" data-testid="case-author-affiliation">{selected.author_affiliation}</p>
                        )}
                      </div>
                    </div>
                  )}
                  {selected.detail && selected.detail.length > 0 ? (
                    selected.detail.map((item, i) => (
                      <div key={i}>
                        <p className="font-semibold text-[#361a54]">{item.heading}</p>
                        <p className="mt-1">{item.content}</p>
                      </div>
                    ))
                  ) : (
                    <p>{selected.summary}</p>
                  )}
                  {selected.focus && (
                    <p className="text-sm text-muted-foreground">
                      <span className="font-semibold text-[#361a54]">Focus: </span>{selected.focus}
                    </p>
                  )}
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 text-xs">
                  <div className="flex flex-wrap gap-1.5">
                    {selected.tags?.map((tag) => (
                      <Badge key={tag} variant="outline" className="border-[#d4c5e6] bg-[#f5f0fa] text-xs font-medium text-[#361a54]">{tag}</Badge>
                    ))}
                  </div>
                  {selected.access_mode === "request" ? (
                    <RequestAccessMailto caseId={selected.case_id} caseTitle={selected.title} />
                  ) : selected.pdf_url ? (
                    <Button asChild size="sm" variant="outline" className="border-[#d4c5e6] bg-[#f5f0fa] text-[#361a54] hover:bg-[#e8e0f0]" data-testid="read-full-case-btn">
                      <a href={selected.pdf_url} target="_blank" rel="noreferrer" onClick={() => trackPdfClick(selected.case_id)}>
                        <FileText className="mr-1.5 h-3.5 w-3.5" />Read full case (PDF)
                      </a>
                    </Button>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="card-elevated">
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                Select a case study from the list to see details, video and links.
              </CardContent>
            </Card>
          )}
        </div>
      </section>
    </div>
  );
}


export function CcLicense({ variant = "notice" }) {
  const href = "https://creativecommons.org/licenses/by-nc/4.0/";
  const badge = "https://mirrors.creativecommons.org/presskit/buttons/88x31/png/by-nc.png";
  if (variant === "footer") {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-2 hover:text-[#361a54] hover:underline"
        data-testid="cc-license-footer"
      >
        <img src={badge} alt="CC BY-NC 4.0" className="h-5 w-auto shrink-0" loading="lazy" />
        <span>All rights reserved. Course content is licensed for non-commercial distribution under CC BY-NC 4.0</span>
      </a>
    );
  }
  return (
    <div
      className="flex items-start gap-3 rounded-lg border border-[#e8e0f0] bg-[#f5f0fa]/60 px-4 py-3 text-xs leading-relaxed text-slate-600"
      data-testid="cc-license-notice"
    >
      <a href={href} target="_blank" rel="noreferrer" className="shrink-0">
        <img src={badge} alt="Creative Commons Attribution-NonCommercial 4.0 International License" className="mt-0.5 h-6 w-auto" loading="lazy" />
      </a>
      <p>
        <span className="font-medium text-[#361a54]">All rights reserved.</span> Except where otherwise noted, the content of this course — including all videos, text, and downloadable materials — is licensed under a{" "}
        <a href={href} target="_blank" rel="noreferrer" className="font-medium text-[#1a8bb3] hover:underline">
          Creative Commons Attribution-NonCommercial 4.0 International License (CC BY-NC 4.0)
        </a>.
      </p>
    </div>
  );
}

function toBullets(text) {
  return (text || "")
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
}

export function ComingSoonPage() {
  const [settings, setSettings] = useState({ coming_soon_courses: "", coming_soon_features: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("/settings")
      .then((r) => setSettings(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const courses = toBullets(settings.coming_soon_courses);
  const features = toBullets(settings.coming_soon_features);

  return (
    <div data-testid="coming-soon-page" className="flex flex-1 flex-col gap-6 md:gap-8">
      <section className="space-y-3 md:space-y-4">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#4a2d73]">Coming soon</p>
        <h1 className="text-balance text-2xl font-semibold tracking-tight text-[#361a54] md:text-3xl">
          What we're building next
        </h1>
        <p className="max-w-prose text-sm text-muted-foreground md:text-base">
          A preview of the courses and platform features currently in development for the DPI Governance Applied Learning programme.
        </p>
      </section>

      {loading ? (
        <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>
      ) : (
        <section className="grid gap-4 md:grid-cols-2 md:gap-6">
          <Card className="card-elevated flex flex-col" data-testid="coming-soon-courses-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base font-semibold text-[#361a54]">
                <BookOpen className="h-4 w-4 text-[#1a8bb3]" />
                Courses in development
              </CardTitle>
              <CardDescription className="text-sm leading-relaxed">
                New self-paced courses we are designing and producing.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-sm leading-relaxed text-slate-700">
              {courses.length > 0 ? (
                <ul className="list-disc space-y-2 pl-5" data-testid="coming-soon-courses-list">
                  {courses.map((item, i) => (<li key={i}>{item}</li>))}
                </ul>
              ) : (
                <p className="text-muted-foreground">Nothing to show yet — check back soon.</p>
              )}
            </CardContent>
          </Card>

          <Card className="card-elevated flex flex-col" data-testid="coming-soon-features-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base font-semibold text-[#361a54]">
                <Rocket className="h-4 w-4 text-[#1a8bb3]" />
                Features in development
              </CardTitle>
              <CardDescription className="text-sm leading-relaxed">
                Platform improvements coming to the learning experience.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-sm leading-relaxed text-slate-700">
              {features.length > 0 ? (
                <ul className="list-disc space-y-2 pl-5" data-testid="coming-soon-features-list">
                  {features.map((item, i) => (<li key={i}>{item}</li>))}
                </ul>
              ) : (
                <p className="text-muted-foreground">Nothing to show yet — check back soon.</p>
              )}
            </CardContent>
          </Card>
        </section>
      )}
    </div>
  );
}
